import React, { useState } from "react";
import { toast } from "sonner";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar, Plus } from "lucide-react";
import { fmtDate } from "@/lib/communities";

const TYPES = ["Admin Raid Base", "Bradley Event", "Cargo Event", "Oil Rig Event", "Treasure Hunt",
    "Fishing Tournament", "Build Competition", "PVP Tournament", "Bow Tournament", "Giveaway", "Purge", "Custom Event"];

export default function Events() {
    const { data: communities } = useFetch("/communities");
    const { data: events, reload } = useFetch("/events");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        community_key: "", type: TYPES[0], title: "", description: "", date: "",
        location: "", rewards: "", team_size: 1, registration_limit: 0, rules: "", post_to_discord: true,
    });
    const [busy, setBusy] = useState(false);
    if (!communities || !events) return <Loader />;
    const upd = (k, v) => setForm({ ...form, [k]: v });
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || k;

    const create = async () => {
        if (!form.community_key || !form.title || !form.date) { toast.error("Community, title and date required"); return; }
        setBusy(true);
        try {
            const payload = { ...form, date: new Date(form.date).toISOString(), team_size: +form.team_size, registration_limit: +form.registration_limit };
            const { data } = await api.post("/events", payload);
            reload();
            toast.success("Event created", { description: form.post_to_discord ? (data.discord?.success ? "Posted to Discord #events" : "Discord post skipped") : "Saved" });
            setOpen(false);
            setForm({ ...form, title: "", description: "", date: "", rules: "", rewards: "" });
        } catch (e) {
            toast.error("Create failed", { description: e.response?.data?.detail || "" });
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-events">
            <PageHeader title="Events" subtitle="Create events and sync formatted announcements to Discord."
                action={<Button data-testid="new-event-btn" onClick={() => setOpen(!open)} className="font-heading uppercase bg-burnt text-white hover:bg-burnt/90"><Plus size={15} className="mr-1" /> New Event</Button>} />

            {open && (
                <SectionCard title="Create Event" testId="event-composer" className="mb-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Community">
                            <Select value={form.community_key} onValueChange={(v) => upd("community_key", v)}>
                                <SelectTrigger data-testid="event-community" className="bg-background border-border"><SelectValue placeholder="Select community" /></SelectTrigger>
                                <SelectContent>{communities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="Event Type">
                            <Select value={form.type} onValueChange={(v) => upd("type", v)}>
                                <SelectTrigger data-testid="event-type" className="bg-background border-border"><SelectValue /></SelectTrigger>
                                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="Title"><Input data-testid="event-title" value={form.title} onChange={(e) => upd("title", e.target.value)} className="bg-background border-border" /></Field>
                        <Field label="Date & Time"><Input data-testid="event-date" type="datetime-local" value={form.date} onChange={(e) => upd("date", e.target.value)} className="bg-background border-border" /></Field>
                        <Field label="Location"><Input value={form.location} onChange={(e) => upd("location", e.target.value)} className="bg-background border-border" /></Field>
                        <Field label="Rewards"><Input value={form.rewards} onChange={(e) => upd("rewards", e.target.value)} className="bg-background border-border" /></Field>
                        <Field label="Team Size"><Input type="number" value={form.team_size} onChange={(e) => upd("team_size", e.target.value)} className="bg-background border-border" /></Field>
                        <Field label="Registration Limit"><Input type="number" value={form.registration_limit} onChange={(e) => upd("registration_limit", e.target.value)} className="bg-background border-border" /></Field>
                        <div className="md:col-span-2"><Field label="Description"><Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} className="bg-background border-border" rows={2} /></Field></div>
                        <div className="md:col-span-2"><Field label="Rules"><Textarea value={form.rules} onChange={(e) => upd("rules", e.target.value)} className="bg-background border-border" rows={2} /></Field></div>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-foreground mt-3 cursor-pointer">
                        <Checkbox data-testid="event-discord" checked={form.post_to_discord} onCheckedChange={(v) => upd("post_to_discord", !!v)} /> Post event to Discord
                    </label>
                    <Button data-testid="event-create" disabled={busy} onClick={create} className="mt-3 font-heading uppercase bg-hazard text-black hover:bg-hazard/90">Create Event</Button>
                </SectionCard>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {events.map((e) => (
                    <div key={e.id} data-testid={`admin-event-${e.id}`} className="tactical-card rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <span className="rounded-sm border border-hazard/40 bg-hazard/10 px-2 py-0.5 label-mono text-hazard">{e.type}</span>
                            {e.discord_posted ? <StatusBadge status="success" label="ON DISCORD" /> : <span className="label-mono text-muted-foreground">Dashboard only</span>}
                        </div>
                        <h3 className="font-heading text-lg font-bold uppercase text-foreground mt-2">{e.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Calendar size={13} className="text-hazard" /> {fmtDate(e.date)} · {nameFor(e.community_key)}
                        </div>
                        <div className="label-mono text-muted-foreground mt-2">{e.registrations} registered</div>
                    </div>
                ))}
            </div>
            <Footer />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="label-mono text-muted-foreground">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
